import { SimulationNeedsViews } from './SimulationNeedsViews';
import { initialColorDetails, mergeLoadedColorDetails } from "@/lib/costSheetColorPresets";
import { MaterialColorMatrix } from "./MaterialColorMatrix";
import { colorVariantFields, nextVariantKey } from "@/lib/materialColors";
import React from "react";
import { AlertTriangle, ArrowLeft, Factory, Loader2, Plus, Printer, Save, Search, Trash2, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { CostSheetManager } from "./CostSheetManager";
import { useToast } from "@/components/ui/use-toast";
import { useColorSettings } from "@/contexts/ColorSettingsContext";
import { buildPrintModelFromEntries, buildStudyItemFromEntry } from "@/lib/manufacturingSimulationPrint";
import { SimulationStudyReport } from "@/components/manufacturing/SimulationStudyReport";
import { generateSimulationPdf, SIMULATION_REPORT_TYPES } from "@/lib/manufacturingSimulationPdf";
import { SimulationCostSummary } from "@/components/manufacturing/SimulationCostSummary";
import { SimulationPrintDialog } from "@/components/manufacturing/SimulationPrintDialog";
import { SimulationAlfatexCapture } from "@/components/manufacturing/SimulationAlfatexCapture";
import { SimulationForecastCapture } from "@/components/manufacturing/SimulationForecastCapture";
import { SimulationHistory } from "@/components/manufacturing/SimulationHistory";
import { SimulationMagicSourceSelector } from "@/components/manufacturing/SimulationMagicSourceSelector";
import { SimulationProductPicker } from "@/components/manufacturing/SimulationProductPicker";
import { SimulationSizeColorMatrix } from "@/components/manufacturing/SimulationSizeColorMatrix";
import {
  calculateSimulationProduct,
  fetchSimulationProduct,
  saveManufacturingSimulation,
  summarizeSimulationItems,
} from "@/lib/api/manufacturingSimulation";
import {
  convertSimulationToProductionOrder,
  fetchProductionOrder,
  saveProductionOrder as saveProductionOrderApi,
} from "@/lib/api/mfgProductionOrders";
import { magicFillQuantities } from "@/lib/constants";

const today = () => new Date().toISOString().slice(0, 10);
const productImage = (product) => product?.images?.[product.main_image_index || 0] || product?.images?.[0] || "";
const articleMatchesFilter = (entry, term) => {
  const search = String(term || "").trim().toLowerCase();
  if (!search) return true;
  const product = entry?.product || {};
  return [product.code, product.description].some((value) => String(value || "").toLowerCase().includes(search));
};
const defaultDetails = (product = {}) => ({
  manufacturer_reference: "",
  pattern_code: "",
  season_snapshot: product.temporada || "",
  notes: "",
  color_contrasts: {},
  color_variants: {},
  simulated_colors: [],
});

function ProductSimulationCard({
  entry,
  mode,
  productionOrderMode = false,
  onQuantityChange,
  onFillRow,
  onDetailChange,
  onContrastChange,
  onGridAdd,
  onGridRemove,
  onRemove,
  onViewCostSheet,
}) {
  const { product, costSheet, hasValidatedCostSheet, quantities, details, loadingCostSheet } = entry;
  const image = productImage(product);
  const { colorSettings } = useColorSettings();
  const studyItem = React.useMemo(() => productionOrderMode ? null : buildStudyItemFromEntry(entry, colorSettings), [entry, colorSettings, productionOrderMode]);
  return (
    <div className="overflow-hidden rounded-lg border-2 border-blue-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-center gap-3 border-b bg-blue-50/40 p-3">
          {image ? <img src={image} alt={product.description} className="h-24 w-24 flex-shrink-0 rounded bg-white object-contain" /> : <div className="flex h-24 w-24 flex-shrink-0 items-center justify-center rounded bg-gray-100 text-xs text-gray-500">Sin imagen</div>}
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2"><h3 className="font-semibold">{product.code} · {product.description}</h3><Badge className={!loadingCostSheet && costSheet && !hasValidatedCostSheet ? "border-red-200 bg-red-100 text-red-800" : undefined} variant={hasValidatedCostSheet ? "secondary" : "outline"}>{loadingCostSheet ? "Cargando escandallo…" : !costSheet ? "Sin escandallo" : hasValidatedCostSheet ? "Escandallo validado" : "Escandallo no validado"}</Badge></div>
            <p className="mt-1 text-sm text-gray-600">{product.season?.descrip || product.temporada || "Sin temporada"} · {product.composicion || "Sin composición"}</p>
          </div>
          {onViewCostSheet && <Button type="button" variant="outline" size="sm" disabled={loadingCostSheet || !costSheet || !product.id} onClick={() => onViewCostSheet(product)} aria-label={`Ver escandallo del artículo ${product.code}`}>Ver escandallo del artículo</Button>}
          {mode === "group" && <Button type="button" variant="outline" size="sm" onClick={() => onRemove(product.id)} title="Eliminar artículo"><Trash2 className="h-4 w-4" /></Button>}
        </div>
        <div className="space-y-4 p-4">
            {loadingCostSheet && <div className="flex items-center gap-2 rounded border bg-blue-50 p-3 text-sm text-blue-900"><Loader2 className="h-4 w-4 animate-spin" />Cargando escandallo y costes del artículo...</div>}
            {!loadingCostSheet && !costSheet && <div className="flex gap-2 rounded border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-900"><AlertTriangle className="h-4 w-4" />Este artículo no tiene escandallo; su coste será cero.</div>}
            {!productionOrderMode && costSheet && !hasValidatedCostSheet && <div className="flex gap-2 rounded border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-900"><AlertTriangle className="h-4 w-4" />El escandallo de este artículo aún no está validado.</div>}

            <fieldset disabled={loadingCostSheet} className="mt-0 space-y-4 min-w-0">
      <div>
        <h4 className="mb-2 font-semibold">Cantidades a fabricar por color y talla</h4>
        <SimulationSizeColorMatrix
          colors={details.simulated_colors}
          quantities={quantities}
          contrasts={details.color_contrasts}
          variants={details.color_variants}
          hideContrasts
          allowVariants
          onChange={(key, value) => onQuantityChange(product.id, key, value)}
          onFillRow={(color, value) => onFillRow(product.id, color, value)}
          onContrastChange={(color, field, value) => onContrastChange(product.id, color, field, value)}
          onAddColor={(color) => onGridAdd(product.id, "simulated_colors", color)}
          onRemoveColor={(color) => onGridRemove(product.id, "simulated_colors", color)}
        />
        <MaterialColorMatrix sheet={costSheet} colors={details.simulated_colors} variants={details.color_variants}
          contrasts={details.color_contrasts} onChange={(value) => onDetailChange(product.id, "color_variants", value)} />
      </div>
            </fieldset>

            {!productionOrderMode && <SimulationStudyReport item={studyItem} />}
        </div>
    </div>
  );
}

export function ManufacturingSimulator({
  mode = "group",
  initialProductId = null,
  hideProductSelector = false,
  productionOrderMode = false,
  onProductionOrderCreated,
  beforeCloseRef,
}) {
  const [productPickerOpen, setProductPickerOpen] = React.useState(false);
  const [entries, setEntries] = React.useState([]);
  const [costSheetProduct, setCostSheetProduct] = React.useState(null);
  const [editingId, setEditingId] = React.useState(null);
  const editingVersion = React.useRef(null);
  const [magicMode, setMagicMode] = React.useState(null);
  const [editorOpen, setEditorOpen] = React.useState(mode === "single_product" || productionOrderMode);
  const [simulationDate, setSimulationDate] = React.useState(today());
  const [description, setDescription] = React.useState("");
  const [notes, setNotes] = React.useState("");
  const [isSample, setIsSample] = React.useState(false);
  const [loadingProduct, setLoadingProduct] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const savingRef = React.useRef(false);
  const [convertingId, setConvertingId] = React.useState(null);
  const [historyReloadKey, setHistoryReloadKey] = React.useState(0);
  const [articleFilter, setArticleFilter] = React.useState("");
  const [printModel, setPrintModel] = React.useState(null);
  const [printDialogOpen, setPrintDialogOpen] = React.useState(false);
  const [reportType, setReportType] = React.useState(SIMULATION_REPORT_TYPES.FULL);
  const [generatingPdf, setGeneratingPdf] = React.useState(false);
  const { toast } = useToast();
  const { colorSettings } = useColorSettings();
  const isSingleProduct = mode === "single_product";

  const loadProduct = React.useCallback(async (productId, replace = false, initialQuantities = {}, productPreview = null, initialDetails = null) => {
    if (!productId) return;
    const previewDetails = initialDetails || defaultDetails(productPreview || {});
    if (productPreview) {
      const previewEntry = { product: productPreview, costSheet: null, hasValidatedCostSheet: false, quantities: initialQuantities, details: previewDetails, loadingCostSheet: true };
      setEntries((current) => replace ? [previewEntry] : current.some((entry) => entry.product.id === productId) ? current : [...current, previewEntry]);
    }
    setLoadingProduct(true);
    const result = await fetchSimulationProduct(productId);
    setLoadingProduct(false);
    if (result.error || !result.data?.product) {
      setEntries((current) => current.map((entry) => entry.product.id === productId ? { ...entry, loadingCostSheet: false } : entry));
      return toast({ title: "Artículo añadido sin costes", description: "No se pudo cargar su escandallo, pero puedes introducir cantidades.", variant: "destructive" });
    }
    const nextEntry = { ...result.data, quantities: initialQuantities, details: initialDetails || { ...defaultDetails(result.data.product), ...initialColorDetails(result.data.costSheet) }, loadingCostSheet: false };
    setEntries((current) => {
      if (replace) return [nextEntry];
      const existing = current.find((entry) => entry.product.id === productId);
      if (!existing) return [...current, nextEntry];
      return current.map((entry) => entry.product.id === productId ? { ...nextEntry, quantities: entry.quantities, details: mergeLoadedColorDetails(entry.details, previewDetails, nextEntry.details) } : entry);
    });
  }, [toast]);

  React.useEffect(() => {
    if (isSingleProduct && initialProductId) loadProduct(initialProductId, true);
  }, [initialProductId, isSingleProduct, loadProduct]);

  const resetEditor = () => {
    setEditingId(null);
    editingVersion.current = null;
    setProductPickerOpen(false);
    setEntries([]);
    setSimulationDate(today());
    setDescription("");
    setNotes("");
    setIsSample(false);
    setArticleFilter("");
  };

  const createSimulation = () => {
    resetEditor();
    setEditorOpen(true);
  };

  const editSimulation = async (simulation) => {
    resetEditor();
    setEditingId(simulation.id);
    editingVersion.current = simulation.content_version;
    setSimulationDate(simulation.simulation_date || today());
    setDescription(simulation.description || "");
    setNotes(simulation.notes || "");
    setEditorOpen(true);
    for (const item of simulation.items || []) {
      const sizeFields = { P: "quantity_p", M: "quantity_m", L: "quantity_l", XL: "quantity_xl", "2XL": "quantity_2xl", "3XL": "quantity_3xl", "4XL": "quantity_4xl", "5XL": "quantity_5xl" };
      const quantities = Object.fromEntries((item.color_lines || []).flatMap((line) => Object.entries(sizeFields).map(([size, field]) => [`${line.color}::${size}`, Number(line[field] || 0)])));
      const colorContrasts = Object.fromEntries((item.color_lines || []).map((line) => [line.color, { contrast_1: line.contrast_1 || "", contrast_2: line.contrast_2 || "", contrast_3: line.contrast_3 || "" }]));
      const details = {
        manufacturer_reference: item.manufacturer_reference || "",
        pattern_code: item.pattern_code || "",
        season_snapshot: item.season_snapshot || "",
        notes: item.notes || "",
        color_contrasts: colorContrasts,
        color_variants: Object.fromEntries((item.color_lines || []).map((line) => [line.color, colorVariantFields(line)])),
        simulated_colors: [...(item.color_lines || [])].sort((left, right) => left.sort_order - right.sort_order).map((line) => line.color),
      };
      await loadProduct(item.product_id, false, quantities, item.product || null, details);
    }
  };

  const startMagicCapture = () => setMagicMode("selector");

  const handleMagicCreated = () => {
    setMagicMode(null);
    setHistoryReloadKey((value) => value + 1);
  };

  const computedSimulation = React.useMemo(() => summarizeSimulationItems(entries.map((entry) => calculateSimulationProduct(entry.product, entry.costSheet, entry.quantities, entry.details).item)), [entries, productionOrderMode]);
  const needsModel = React.useMemo(() => buildPrintModelFromEntries({ simulationDate, description, notes }, entries, colorSettings), [simulationDate, description, notes, entries, colorSettings]);
  const filteredEntries = React.useMemo(
    () => entries.filter((entry) => articleMatchesFilter(entry, articleFilter)),
    [articleFilter, entries]
  );
  const hasArticleFilter = Boolean(articleFilter.trim());
  const handleQuantityChange = (productId, key, value) => setEntries((current) => current.map((entry) => entry.product.id === productId ? { ...entry, quantities: { ...entry.quantities, [key]: Math.max(0, Math.floor(Number(value || 0))) } } : entry));
  const handleFillRow = (productId, color, value) => {
    const filled = magicFillQuantities(color, value);
    setEntries((current) => current.map((entry) => entry.product.id === productId ? { ...entry, quantities: { ...entry.quantities, ...filled } } : entry));
  };
  const handleDetailChange = (productId, field, value) => setEntries((current) => current.map((entry) => entry.product.id === productId ? { ...entry, details: { ...entry.details, [field]: value } } : entry));
  const handleContrastChange = (productId, color, field, value) => setEntries((current) => current.map((entry) => entry.product.id === productId ? {
    ...entry,
    details: {
      ...entry.details,
      color_contrasts: {
        ...entry.details.color_contrasts,
        [color]: { ...(entry.details.color_contrasts?.[color] || {}), [field]: value },
      },
    },
  } : entry));
  const normalizeGridLabel = (value) => String(value || "").trim().replaceAll("::", "-");
  const handleGridAdd = (productId, field, rawValue) => {
    const baseColor = normalizeGridLabel(rawValue);
    const value = nextVariantKey(baseColor, entries.find((row) => row.product.id === productId)?.details[field] || []);
    const entry = entries.find((row) => row.product.id === productId);
    if (!baseColor) {
      toast({ title: "Dato obligatorio", description: "Introduce un color.", variant: "destructive" });
      return false;
    }
    if (entry?.details[field]?.some((item) => item.toLocaleLowerCase() === value.toLocaleLowerCase())) {
      toast({ title: "Dato duplicado", description: `"${value}" ya existe en el grid.`, variant: "destructive" });
      return false;
    }
    setEntries((current) => current.map((row) => row.product.id === productId ? { ...row, details: { ...row.details, [field]: [...row.details[field], value], color_variants: { ...row.details.color_variants, [value]: { variant_id: crypto.randomUUID(), garment_color: baseColor, material_colors: {} } } } } : row));
    return true;
  };
  const handleGridRemove = (productId, field, value) => {
    setEntries((current) => current.map((row) => {
      if (row.product.id !== productId) return row;
      const nextQuantities = Object.fromEntries(Object.entries(row.quantities).filter(([key]) => {
        const [color] = key.split("::");
        return color !== value;
      }));
      const nextContrasts = { ...(row.details.color_contrasts || {}) };
      delete nextContrasts[value];
      return {
        ...row,
        quantities: nextQuantities,
        details: {
          ...row.details,
          [field]: row.details[field].filter((item) => item !== value),
          color_contrasts: nextContrasts,
        },
      };
    }));
  };

  const printSimulation = () => {
    const model = buildPrintModelFromEntries({ simulationDate, description, notes }, entries, colorSettings);
    if (!model.items.length) return toast({ title: "Nada que imprimir", description: "Añade cantidades a algún artículo antes de imprimir.", variant: "destructive" });
    setPrintModel(model);
    setPrintDialogOpen(true);
  };

  const generateStudyPdf = async (selectedReportType = reportType) => {
    setGeneratingPdf(true);
    try {
      await generateSimulationPdf(buildPrintModelFromEntries({ simulationDate, description, notes }, entries, colorSettings), { reportType: selectedReportType });
    } catch (error) {
      toast({ title: "Error al generar el PDF", description: error.message || "No se pudo generar el informe.", variant: "destructive" });
    } finally {
      setGeneratingPdf(false);
    }
  };

  const saveSimulation = async ({ keepOpen = false } = {}) => {
    if (savingRef.current || loadingProduct) return false;
    savingRef.current = true;
    setSaving(true);
    try {
      const result = await saveManufacturingSimulation({ id: editingId, expectedVersion: editingVersion.current, simulationDate, description, mode, notes, items: computedSimulation.items, totals: computedSimulation.totals });
      if (result.error) { toast({ title: "Error", description: result.error.message || "No se pudo guardar", variant: "destructive" }); return false; }
      editingVersion.current = result.data.content_version;
      toast({ title: editingId ? "Simulación actualizada" : "Simulación creada" });
      setHistoryReloadKey((value) => value + 1);
      if (!isSingleProduct && !keepOpen) {
        setEditorOpen(false);
        resetEditor();
      } else {
        setEditingId(result.data.id);
      }
      return true;
    } catch (error) {
      toast({ title: "Error", description: error.message || "No se pudo guardar", variant: "destructive" });
      return false;
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const saveProductionOrder = async ({ keepOpen = false } = {}) => {
    if (savingRef.current || loadingProduct) return false;
    savingRef.current = true;
    setSaving(true);
    try {
      const result = await saveProductionOrderApi({
        isSample,
        id: editingId,
        orderDate: simulationDate,
        description,
        observations: notes,
        sourceType: "manual",
        items: computedSimulation.items,
      });
      if (result.error) { toast({ title: "Error", description: result.error.message || "No se pudo crear la orden", variant: "destructive" }); return false; }

      const detail = await fetchProductionOrder(result.data);
      const number = detail.data?.number || "OF";
      toast({ title: "Orden de fabricación creada", description: number });
      setEditingId(result.data);
      if (!keepOpen) onProductionOrderCreated?.(detail.data || { id: result.data, number });
      if (!isSingleProduct && !keepOpen) {
        resetEditor();
      }
      return true;
    } catch (error) {
      toast({ title: "Error", description: error.message || "No se pudo guardar la orden", variant: "destructive" });
      return false;
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  React.useEffect(() => {
    if (!beforeCloseRef) return;
    beforeCloseRef.current = productionOrderMode ? saveProductionOrder : saveSimulation;
    return () => { beforeCloseRef.current = null; };
  });

  const convertToProductionOrder = async (simulation) => {
    if (!simulation?.id) return;
    if (!window.confirm(`¿Convertir la simulación "${simulation.description}" en orden de fabricación real?`)) return;
    setConvertingId(simulation.id);
    const result = await convertSimulationToProductionOrder(simulation.id, {
      orderDate: today(),
      expectedVersion: simulation.content_version,
      observations: simulation.description,
    });
    setConvertingId(null);
    if (result.error) return toast({ title: "Error", description: result.error.message || "No se pudo convertir la simulación", variant: "destructive" });

    setHistoryReloadKey((current) => current + 1);
    const detail = await fetchProductionOrder(result.data);
    const number = detail.data?.number || "OF";
    toast({ title: "Orden de fabricación creada", description: number });
    onProductionOrderCreated?.(detail.data || { id: result.data, number });
  };

  if (magicMode && !isSingleProduct && !productionOrderMode) {
    if (magicMode === "sales") return <SimulationForecastCapture onCancel={() => setMagicMode("selector")} onCreated={handleMagicCreated} />;
    if (magicMode === "alfatex") return <SimulationAlfatexCapture onCancel={() => setMagicMode("selector")} onCreated={handleMagicCreated} />;
    return (
      <SimulationMagicSourceSelector
        onCancel={() => setMagicMode(null)}
        onSelectSales={() => setMagicMode("sales")}
        onSelectAlfatex={() => setMagicMode("alfatex")}
      />
    );
  }

  if (!editorOpen && !isSingleProduct && !productionOrderMode) {
    return (
      <SimulationHistory
        reloadKey={historyReloadKey}
        onCreate={createSimulation}
        onMagicCreate={startMagicCapture}
        onEdit={editSimulation}
        onConvert={convertToProductionOrder}
        convertingId={convertingId}
      />
    );
  }

  return (
    <div className="space-y-3">
      {!productionOrderMode && <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border bg-white px-4 py-3 shadow-sm">
        <h2 className="text-xl font-semibold">{editingId ? "Editar simulación" : "Nueva simulación"}</h2>
        <div className="flex flex-wrap items-center gap-2">
          <select aria-label="Tipo de informe" value={reportType} onChange={(event) => setReportType(event.target.value)} className="h-10 rounded-md border bg-white px-3 text-sm">
            <option value={SIMULATION_REPORT_TYPES.FULL}>Informe completo</option>
            <option value={SIMULATION_REPORT_TYPES.NEEDS_BY_PROCESS}>Necesidades por procesos</option>
            <option value={SIMULATION_REPORT_TYPES.NEEDS_BY_SUPPLIER}>Necesidades por proveedor</option>
          </select>
          <Button type="button" variant="outline" onClick={() => generateStudyPdf()} disabled={generatingPdf || loadingProduct || !computedSimulation.items.length}>{generatingPdf ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Printer className="mr-2 h-4 w-4" />}{generatingPdf ? "Generando..." : "Imprimir"}</Button>
          <Button type="button" variant="outline" onClick={() => generateStudyPdf(SIMULATION_REPORT_TYPES.COMPACT)} disabled={generatingPdf || loadingProduct || !computedSimulation.items.length}><Printer className="mr-2 h-4 w-4" />Imprimir compacto</Button>
        {!isSingleProduct && <Button type="button" variant="outline" disabled={saving || loadingProduct} onClick={saveSimulation}><ArrowLeft className="mr-2 h-4 w-4" />Volver a simulaciones</Button>}
        </div>
      </div>}
      <div className="rounded-lg border bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="grid flex-1 gap-3 md:grid-cols-[160px_1fr_1fr]">
            <div><label className="mb-1 block text-sm font-medium">Fecha</label><Input aria-label="Fecha" type="date" value={simulationDate} onChange={(event) => setSimulationDate(event.target.value)} /></div>
            <div><label className="mb-1 block text-sm font-medium">Descripción</label><Input aria-label="Descripción" value={description} onChange={(event) => setDescription(event.target.value)} placeholder={productionOrderMode ? "Descripción opcional" : "Descripción de la simulación"} /></div>
            {productionOrderMode && <label className="flex items-center gap-2 text-sm font-medium">
              <span className="shrink-0">Tipo de fabricación:</span>
              <select aria-label="Tipo de fabricación" className="h-10 min-w-0 flex-1 rounded-md border bg-background px-3" value={isSample ? 'samples' : 'standard'}
                onChange={(event) => setIsSample(event.target.value === 'samples')}>
                <option value="standard">Normal</option><option value="samples">Muestras</option>
              </select>
            </label>}
            <div><label className="mb-1 block text-sm font-medium">Observaciones</label><Input aria-label="Observaciones" value={notes} onChange={(event) => setNotes(event.target.value)} placeholder="Observaciones opcionales" /></div>
          </div>
        </div>
      </div>

      {!hideProductSelector && !isSingleProduct && (
        <div className="space-y-3">
          <Button type="button" variant="outline" aria-expanded={productPickerOpen} onClick={() => setProductPickerOpen((open) => !open)}>
            {productPickerOpen ? <X className="mr-2 h-4 w-4" /> : <Plus className="mr-2 h-4 w-4" />}
            {productPickerOpen ? "Cerrar búsqueda" : "Añadir artículo"}
          </Button>
          {productPickerOpen && <SimulationProductPicker compact productionOrderMode={productionOrderMode} onAddProduct={(product) => {
            loadProduct(product.id, false, {}, product);
            setProductPickerOpen(false);
          }} disabledProductIds={entries.map((entry) => entry.product.id)} />}
        </div>
      )}
      <div className="space-y-4">
        {entries.length > 0 && (
          <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <h3 className="text-lg font-semibold">{productionOrderMode ? "Artículos añadidos a la orden" : "Artículos añadidos a la simulación"}</h3>
              <p className="text-sm text-gray-600">Edita las cantidades y revisa los costes de cada artículo.</p>
            </div>
            {entries.length > 1 && (
              <div className="w-full lg:max-w-md">
                <label className="mb-1 block text-sm font-medium">Filtrar por artículo</label>
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <Input
                    className="pl-8 pr-9"
                    value={articleFilter}
                    onChange={(event) => setArticleFilter(event.target.value)}
                    placeholder="Código o descripción"
                  />
                  {hasArticleFilter && (
                    <button
                      type="button"
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                      onClick={() => setArticleFilter("")}
                      title="Quitar filtro"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  {hasArticleFilter
                    ? `Mostrando ${filteredEntries.length} de ${entries.length} artículos`
                    : `${entries.length} artículos`}
                </p>
              </div>
            )}
          </div>
        )}
        {!productionOrderMode && entries.length > 0 && <SimulationNeedsViews model={needsModel} beforeClose={() => saveSimulation({ keepOpen: true })} />}
        {filteredEntries.map((entry) => (
          <ProductSimulationCard
            key={entry.product.id}
            entry={entry}
            mode={mode}
            productionOrderMode={productionOrderMode}
            onViewCostSheet={mode === "group" && !productionOrderMode ? setCostSheetProduct : undefined}
            onQuantityChange={handleQuantityChange}
            onFillRow={handleFillRow}
            onDetailChange={handleDetailChange}
            onContrastChange={handleContrastChange}
            onGridAdd={handleGridAdd}
            onGridRemove={handleGridRemove}
            onRemove={(id) => setEntries((current) => current.filter((row) => row.product.id !== id))}
          />
        ))}
        {entries.length > 0 && hasArticleFilter && !filteredEntries.length && (
          <div className="rounded-lg border border-dashed bg-white p-8 text-center text-sm text-gray-500">
            Ningún artículo coincide con "{articleFilter.trim()}".
          </div>
        )}
        {!entries.length && !productionOrderMode && <div className="rounded-lg border border-dashed bg-white p-8 text-center text-sm text-gray-500">Añade uno o más artículos para iniciar el estudio de costes.</div>}
      </div>
      {loadingProduct && <div className="rounded-lg border bg-white p-4 text-sm text-gray-600">Cargando costes del artículo...</div>}
      {<SimulationCostSummary title={productionOrderMode ? "Resumen de fabricación" : undefined} totals={computedSimulation.totals} productCount={computedSimulation.items.length} itemCount={computedSimulation.items.reduce((sum, item) => sum + item.color_lines.length, 0)} />}
      <div className="flex justify-end gap-2">
        {productionOrderMode && <Button type="button" variant="outline" onClick={printSimulation} disabled={!computedSimulation.items.length}><Printer className="mr-2 h-4 w-4" />Imprimir</Button>}
        <Button
          type="button"
          onClick={productionOrderMode ? saveProductionOrder : saveSimulation}
          disabled={saving || loadingProduct || !simulationDate || (!productionOrderMode && !description.trim()) || !computedSimulation.items.length}
        >
          {productionOrderMode ? <Factory className="mr-2 h-4 w-4" /> : <Save className="mr-2 h-4 w-4" />}
          {saving ? "Guardando..." : productionOrderMode ? "Crear orden fabricación" : editingId ? "Guardar cambios" : "Crear simulación"}
        </Button>
      </div>
      <Dialog open={Boolean(costSheetProduct)} onOpenChange={(open) => { if (!open) setCostSheetProduct(null); }}>
        <DialogContent className="w-[95vw] max-w-7xl max-h-[90vh] overflow-y-auto">
          <DialogTitle>Escandallo · {costSheetProduct?.code}</DialogTitle>
          <DialogDescription>Consulta del escandallo actual del artículo. Al cerrar volverás a la simulación con tus cambios pendientes.</DialogDescription>
          {costSheetProduct && <CostSheetManager key={costSheetProduct.id} productId={costSheetProduct.id} hideProductSelector readOnly />}
        </DialogContent>
      </Dialog>
      <SimulationPrintDialog open={printDialogOpen} onOpenChange={async (open) => { if (open || await (productionOrderMode ? saveProductionOrder : saveSimulation)({ keepOpen: true })) setPrintDialogOpen(open); }} model={printModel} />
    </div>
  );
}
